import { useTranslation } from "react-i18next";
import { cn } from "~/utils";
import { InputWithClear } from "~/components/input/input-with-clear";
import { SearchTypeSelect, type SearchTypeOption } from "./search-type-select";

export type { SearchTypeOption };

interface SearchBarProps<T extends string = string> {
  searchType?: T;
  searchTypeOptions?: SearchTypeOption<T>[];
  onSearchTypeChange?: (value: T) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onSearchSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  showSearchType?: boolean;
  onClear?: () => void;
  className?: string;
  autoFocus?: boolean;
  showEnterHint?: boolean;
  showClearOnHover?: boolean;
}

export const SearchBar = <T extends string = string>({
  searchType,
  searchTypeOptions = [],
  onSearchTypeChange,
  searchQuery,
  onSearchQueryChange,
  onSearchSubmit,
  placeholder,
  disabled = false,
  showSearchType = true,
  onClear,
  className,
  autoFocus,
  showEnterHint = false,
  showClearOnHover = false,
}: SearchBarProps<T>) => {
  const { t } = useTranslation("components");
  const defaultPlaceholder = t("searchBar.placeholder");
  const hasSearchTypeSelector = showSearchType && searchTypeOptions.length > 0;

  const inputClassName = hasSearchTypeSelector
    ? "border-l-0 rounded-lg rounded-l-none h-full"
    : "rounded-lg h-full";

  return (
    <div className={cn("flex", className)}>
      {hasSearchTypeSelector && (
        <SearchTypeSelect
          value={searchType}
          onValueChange={onSearchTypeChange}
          options={searchTypeOptions}
          disabled={disabled}
        />
      )}
      <InputWithClear
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        onKeyDown={(event) => {
          if (onSearchSubmit && event.key === "Enter") {
            event.preventDefault();
            onSearchSubmit();
          }
        }}
        placeholder={placeholder ?? defaultPlaceholder}
        disabled={disabled}
        onClear={onClear}
        showEnterHint={showEnterHint}
        showClearOnHover={showClearOnHover}
        className={inputClassName}
        autoFocus={autoFocus}
      />
    </div>
  );
};
